import { FC } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useSpecificBroker } from "../../dataHooks/brokerQueries";
import { EntityMergerProvider } from "../../../../auth/entityMergerContext";
import { correctedEntityPath } from "../../../../utils/entityPaths";
import { SignOffPage } from "./signOffPage";

type Params = {
  id?: string;
};

export const BrokerSignOff: FC = () => {
  const { id } = useParams<Params>();
  const { pathname } = useLocation();

  const { data: broker, isLoading } = useSpecificBroker(id, {
    // Poll while on the sign-off page so a VP signing off picks up a rejection or another
    // signature made by someone else without needing a manual reload.
    refetchInterval: 30000
  });

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (broker === undefined) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Broker not found</Alert>
      </Box>
    );
  }

  // Sign-off links in the mails point at /mergers for a merger; an older link may not. Only once
  // the loaded entity is the one in the URL (keepPreviousData, see the detail page).
  const correctedPath = correctedEntityPath(
    "broker",
    pathname,
    broker.isMerger
  );
  if (correctedPath && broker.id === id) {
    return <Navigate to={correctedPath} replace />;
  }

  return (
    <EntityMergerProvider value={Boolean(broker.isMerger)}>
      <Box flex={1}>
        <SignOffPage specificBroker={broker} key={broker.id} />
      </Box>
    </EntityMergerProvider>
  );
};
